import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ArrowRight, Bell, Flame, LayoutGrid, 
         ClipboardList, BookOpen, BarChart2, Settings, 
         Map, Calendar, UtensilsCrossed, ShoppingBag, 
         History, Coffee, Tag, Package, TrendingUp, Archive } from 'lucide-react';

         import logo from "../assets/img/logo1.png";

/* ─────────────────────────────────────────────
   All styles self-contained — no external CSS
───────────────────────────────────────────── */
const NAV_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Fraunces:wght@700;800&display=swap');

  :root {
    --rw-tomato:    #ff4d2e;
    --rw-ember:     #ff6b3d;
    --rw-saffron:   #ff9f1c;
    --rw-mint:      #00c896;
    --rw-mint-dim:  #00a87e;
    --rw-white:     #ffffff;
    --rw-ink:       #1a0f0a;
    --rw-surface:   #ffffff;
    --rw-cream:     #fff9f5;
    --rw-muted:     #8a6f65;
    --rw-border:    rgba(255,77,46,0.10);
    --rw-shadow:    rgba(255,77,46,0.14);
    --rw-nav-h:     64px;
  }

  /* ── Wrapper ── */
  .rw-nav-wrapper {
    position: sticky;
    top: 0;
    z-index: 1000;
    width: 100%;
    background: var(--rw-surface);
    border-bottom: 1px solid rgba(255,77,46,0.08);
    transition: box-shadow 0.3s, background 0.3s;
    font-family: 'Plus Jakarta Sans', sans-serif;
  }

  .rw-nav-wrapper.rw-scrolled {
    box-shadow: 0 4px 32px rgba(255,77,46,0.10), 0 1px 8px rgba(0,0,0,0.06);
    background: rgba(255,255,255,0.97);
    backdrop-filter: blur(12px);
  }

  /* Animated top accent bar */
  .rw-nav-wrapper::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--rw-tomato), var(--rw-saffron), var(--rw-mint), var(--rw-saffron), var(--rw-tomato));
    background-size: 240% 100%;
    animation: rw-bar-slide 4s linear infinite;
  }

  @keyframes rw-bar-slide {
    0%   { background-position: 0% 0%; }
    100% { background-position: 240% 0%; }
  }

  /* ── Inner container ── */
  .rw-nav-container {
    max-width: 1380px;
    margin: 0 auto;
    height: var(--rw-nav-h);
    padding: 0 24px;
    display: flex;
    align-items: center;
    gap: 0;
  }

  /* ── Brand ── */
  .rw-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    text-decoration: none;
    flex-shrink: 0;
    margin-right: 32px;
  }

  .rw-logo-box {
    width: 36px; height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--rw-tomato), var(--rw-saffron));
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 4px 14px var(--rw-shadow);
    flex-shrink: 0;
    transition: transform 0.2s, box-shadow 0.2s;
  }

  .rw-brand:hover .rw-logo-box {
    transform: rotate(-6deg) scale(1.08);
    box-shadow: 0 6px 20px rgba(255,77,46,0.28);
  }

  .rw-brand-name {
    font-family: 'Fraunces', serif;
    font-size: 19px;
    font-weight: 800;
    color: var(--rw-ink);
    letter-spacing: -0.3px;
    line-height: 1;
  }

  .rw-brand-tag {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: var(--rw-tomato);
    display: block;
    margin-top: 1px;
    opacity: 0.8;
  }

  /* ── Nav menu ── */
  .rw-nav-menu {
    display: flex;
    align-items: center;
    list-style: none;
    gap: 2px;
    flex: 1;
    margin: 0; padding: 0;
  }

  /* ── Nav item ── */
  .rw-nav-item {
    position: relative;
  }

  .rw-nav-link {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 8px 13px;
    border-radius: 10px;
    font-size: 13.5px;
    font-weight: 600;
    color: #4a3728;
    text-decoration: none;
    white-space: nowrap;
    transition: color 0.18s, background 0.18s;
    position: relative;
    cursor: pointer;
  }

  .rw-nav-link:hover {
    color: var(--rw-tomato);
    background: rgba(255,77,46,0.06);
  }

  .rw-nav-link.rw-active {
    color: var(--rw-tomato);
    background: rgba(255,77,46,0.08);
  }

  /* Active dot indicator */
  .rw-nav-link.rw-active::after {
    content: '';
    position: absolute;
    bottom: 3px;
    left: 50%; transform: translateX(-50%);
    width: 4px; height: 4px;
    border-radius: 50%;
    background: var(--rw-tomato);
  }

  .rw-nav-icon {
    opacity: 0.65;
    flex-shrink: 0;
    transition: opacity 0.18s;
  }

  .rw-nav-link:hover .rw-nav-icon,
  .rw-nav-link.rw-active .rw-nav-icon {
    opacity: 1;
  }

  .rw-chevron {
    transition: transform 0.22s cubic-bezier(.22,.8,.44,1);
    opacity: 0.55;
    flex-shrink: 0;
  }

  .rw-nav-item:hover .rw-chevron,
  .rw-nav-item.rw-open .rw-chevron {
    transform: rotate(180deg);
    opacity: 1;
  }

  /* ── Mega / regular dropdown ── */
  .rw-submenu {
    position: absolute;
    top: calc(100% + 10px);
    left: 50%;
    transform: translateX(-50%) translateY(-8px);
    background: var(--rw-white);
    border: 1px solid rgba(255,77,46,0.10);
    border-radius: 18px;
    box-shadow:
      0 20px 60px rgba(255,77,46,0.12),
      0 4px 16px rgba(0,0,0,0.08);
    padding: 8px;
    list-style: none;
    min-width: 200px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s, transform 0.22s cubic-bezier(.22,.8,.44,1);
    z-index: 200;
  }

  .rw-submenu.rw-show {
    opacity: 1;
    pointer-events: all;
    transform: translateX(-50%) translateY(0);
  }

  /* Arrow pointer */
  .rw-submenu::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%; transform: translateX(-50%);
    width: 12px; height: 12px;
    background: var(--rw-white);
    border-left: 1px solid rgba(255,77,46,0.10);
    border-top: 1px solid rgba(255,77,46,0.10);
    transform: translateX(-50%) rotate(45deg);
  }

  /* ── Sub link ── */
  .rw-sub-link {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 500;
    color: #3d2416;
    text-decoration: none;
    transition: background 0.15s, color 0.15s, transform 0.15s;
    white-space: nowrap;
  }

  .rw-sub-link:hover {
    background: rgba(255,77,46,0.07);
    color: var(--rw-tomato);
    transform: translateX(3px);
  }

  .rw-sub-icon {
    width: 30px; height: 30px;
    border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255,77,46,0.07);
    flex-shrink: 0;
    transition: background 0.15s;
    color: var(--rw-tomato);
  }

  .rw-sub-link:hover .rw-sub-icon {
    background: rgba(255,77,46,0.14);
  }

  .rw-sub-text { display: flex; flex-direction: column; gap: 1px; }
  .rw-sub-name { font-size: 13px; font-weight: 600; line-height: 1.2; }
  .rw-sub-desc { font-size: 11px; color: var(--rw-muted); font-weight: 400; }

  /* ── Divider in sub ── */
  .rw-sub-divider {
    height: 1px;
    background: rgba(255,77,46,0.07);
    margin: 4px 6px;
  }

  /* ── Right actions ── */
  .rw-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: auto;
    flex-shrink: 0;
  }

  /* Live status pill */
  .rw-live-pill {
    display: flex;
    align-items: center;
    gap: 6px;
    background: rgba(0,200,150,0.09);
    border: 1px solid rgba(0,200,150,0.20);
    border-radius: 100px;
    padding: 5px 12px;
    font-size: 12px;
    font-weight: 600;
    color: var(--rw-mint-dim);
    cursor: default;
    transition: background 0.2s;
  }

  .rw-live-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: var(--rw-mint);
    box-shadow: 0 0 0 2px rgba(0,200,150,0.25);
    animation: rw-pulse-dot 2s ease-in-out infinite;
  }

  @keyframes rw-pulse-dot {
    0%, 100% { box-shadow: 0 0 0 2px rgba(0,200,150,0.25); }
    50%       { box-shadow: 0 0 0 5px rgba(0,200,150,0.10); }
  }

  /* Notification button */
  .rw-notif-btn {
    position: relative;
    width: 38px; height: 38px;
    border-radius: 11px;
    border: 1.5px solid rgba(255,77,46,0.12);
    background: var(--rw-cream);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    color: #5a3a28;
    transition: background 0.18s, border-color 0.18s, transform 0.15s, box-shadow 0.18s;
  }

  .rw-notif-btn:hover {
    background: rgba(255,77,46,0.07);
    border-color: rgba(255,77,46,0.28);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255,77,46,0.12);
  }

  .rw-notif-btn:hover svg {
    animation: rw-bell-ring 0.4s ease;
  }

  @keyframes rw-bell-ring {
    0%,100% { transform: rotate(0); }
    25%     { transform: rotate(18deg); }
    75%     { transform: rotate(-18deg); }
  }

  .rw-dot {
    position: absolute;
    top: 7px; right: 7px;
    width: 8px; height: 8px;
    border-radius: 50%;
    background: var(--rw-tomato);
    border: 2px solid white;
    animation: rw-dot-pop 0.4s cubic-bezier(.22,.8,.44,1) both;
  }

  @keyframes rw-dot-pop {
    from { transform: scale(0); }
    to   { transform: scale(1); }
  }

  /* Vertical divider */
  .rw-divider {
    width: 1px; height: 26px;
    background: rgba(255,77,46,0.12);
  }

  /* CTA Button */
  .rw-cta-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 18px;
    background: linear-gradient(135deg, var(--rw-tomato), #f0641a, var(--rw-saffron));
    border: none;
    border-radius: 12px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: #fff;
    cursor: pointer;
    letter-spacing: 0.2px;
    position: relative;
    overflow: hidden;
    transition: transform 0.15s, box-shadow 0.2s, filter 0.15s;
    box-shadow: 0 4px 16px rgba(255,77,46,0.28), inset 0 1px 0 rgba(255,255,255,0.15);
    text-decoration: none;
  }

  .rw-cta-btn::before {
    content: '';
    position: absolute;
    top: 0; left: -70%;
    width: 55%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent);
    transform: skewX(-18deg);
    transition: left 0.5s;
  }

  .rw-cta-btn:hover::before { left: 130%; }

  .rw-cta-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255,77,46,0.36);
    filter: brightness(1.05);
  }

  .rw-cta-arrow { transition: transform 0.2s; }
  .rw-cta-btn:hover .rw-cta-arrow { transform: translateX(3px); }

  /* ── Responsive ── */
  @media (max-width: 1100px) {
    .rw-live-pill { display: none; }
    .rw-nav-container { padding: 0 16px; }
    .rw-brand { margin-right: 20px; }
  }

  @media (max-width: 900px) {
    .rw-nav-menu .rw-nav-item:nth-child(n+5) { display: none; }
  }

  @media (max-width: 700px) {
    .rw-nav-menu { display: none; }
    .rw-cta-btn span { display: none; }
    .rw-cta-btn { padding: 9px 12px; }
  }
`;

/* ─────────────────────────────────────────────
   Sub-menu data with icons & descriptions
───────────────────────────────────────────── */
const menuItems = [
  {
    name: 'Dashboard',
    path: '/dashboard',
    icon: <LayoutGrid size={14} />,
  },
  {
    name: 'Tables',
    path: '#',
    icon: <Coffee size={14} />,
    sub: [
      { name: 'Tables',    path: '/tables',   icon: <Coffee size={14} />,      desc: 'Visual table layout' },
      { name: 'Floor Plan',    path: '/tables/floor-plan',   icon: <Map size={14} />,      desc: 'Visual table layout' },
      { name: 'Reservations',  path: '/tables/reservation',  icon: <Calendar size={14} />, desc: 'Booking & schedules' },
    ],
  },
  {
    name: 'Orders',
    path: '#',
    icon: <ClipboardList size={14} />,
    sub: [
      { name: 'Dine-in',       path: '/orders',  icon: <UtensilsCrossed size={14} />, desc: 'Active table orders' },
      // { name: 'Takeaway',      path: '/orders',  icon: <ShoppingBag size={14} />,     desc: 'Pickup & delivery' },
      { name: 'Order History', path: '/order-history',  icon: <History size={14} />,         desc: 'Past transactions' },
    ],
  },
  {
    name: 'Menu',
    path: '#',
    icon: <BookOpen size={14} />,
    sub: [
      { name: 'Menu List',    path: '/menu',  icon: <Coffee size={14} />,   desc: 'Manage all dishes' },
      // { name: 'Categories',    path: '/menu',  icon: <Tag size={14} />,      desc: 'Group & organise' },
      { name: 'Stock Manager', path: '/stock',  icon: <Package size={14} />,  desc: 'Stock control' },
    ],
  },
  {
    name: 'Reports',
    path: '/reports',
    icon: <BarChart2 size={14} />,
    // sub: [
    //   { name: 'Daily Sales',     path: '/reports/sales',     icon: <TrendingUp size={14} />, desc: 'Revenue & trends' },
    //   { name: 'Inventory Logs',  path: '/reports/inventory', icon: <Archive size={14} />,    desc: 'Stock movement' },
    // ],
  },
  {
    name: 'Settings',
    path: '/settings',
    icon: <Settings size={14} />,
  },
];

/* ─────────────────────────────────────────────
   Component
───────────────────────────────────────────── */
function Navbar() {
  const location = useLocation();
  const [scrolled,        setScrolled]        = useState(false);
  const [activeDropdown,  setActiveDropdown]  = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/') ? 'rw-active' : '';

  /* Delayed close to allow moving cursor to dropdown */
  const handleMouseEnter = (name) => {
    clearTimeout(timerRef.current);
    setActiveDropdown(name);
  };

  const handleMouseLeave = () => {
    timerRef.current = setTimeout(() => setActiveDropdown(null), 120);
  };

  return (
    <>
      <style>{NAV_STYLES}</style>

      <div className={`rw-nav-wrapper ${scrolled ? 'rw-scrolled' : ''}`}>
        <nav className="rw-nav-container">

          {/* ── Brand ── */}
          <Link className="rw-brand" to="/dashboard">
            {/* <div className="rw-logo-box"> */}
            <div className="">
              {/* <Flame size={18} color="#fff" strokeWidth={2.5} /> */}
              <img src={logo} className='' style={{maxHeight: "50px"}} />
            </div>
            <div>
              <span className="rw-brand-name">Restrowave</span>
              <span className="rw-brand-tag">POS Terminal</span>
            </div>
          </Link>

          {/* ── Nav menu ── */}
          <ul className="rw-nav-menu">
            {menuItems.map((item) => (
              <li
                key={item.name}
                className={`rw-nav-item ${item.sub ? 'rw-has-sub' : ''} ${activeDropdown === item.name ? 'rw-open' : ''}`}
                onMouseEnter={() => handleMouseEnter(item.name)}
                onMouseLeave={handleMouseLeave}
              >
                <Link className={`rw-nav-link ${isActive(item.path)}`} to={item.path}>
                  <span className="rw-nav-icon">{item.icon}</span>
                  {item.name}
                  {item.sub && <ChevronDown size={13} className="rw-chevron" />}
                </Link>

                {/* Dropdown */}
                {item.sub && (
                  <ul
                    className={`rw-submenu ${activeDropdown === item.name ? 'rw-show' : ''}`}
                    onMouseEnter={() => handleMouseEnter(item.name)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {item.sub.map((sub, si) => (
                      <React.Fragment key={sub.name}>
                        {si > 0 && si === item.sub.length - 1 && item.sub.length > 2 && (
                          <li><div className="rw-sub-divider" /></li>
                        )}
                        <li>
                          <Link className="rw-sub-link" to={sub.path}>
                            <span className="rw-sub-icon">{sub.icon}</span>
                            <span className="rw-sub-text">
                              <span className="rw-sub-name">{sub.name}</span>
                              {sub.desc && <span className="rw-sub-desc">{sub.desc}</span>}
                            </span>
                          </Link>
                        </li>
                      </React.Fragment>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>

          {/* ── Right actions ── */}
          <div className="rw-actions">

            {/* Live indicator */}
            <div className="rw-live-pill">
              <span className="rw-live-dot" />
              Kitchen Live
            </div>

            {/* Notifications */}
            <button className="rw-notif-btn" aria-label="Notifications">
              <Bell size={17} strokeWidth={2} />
              <span className="rw-dot" />
            </button>

            <div className="rw-divider" />

            {/* CTA */}
            <Link to="/orders" className="rw-cta-btn">
              <span>New Order</span>
              <ArrowRight size={15} className="rw-cta-arrow" />
            </Link>

          </div>
        </nav>
      </div>
    </>
  );
}

export default Navbar;
